import { useCallback, useEffect, useMemo, useRef } from "react";
import { createRoot, type Root } from "react-dom/client";
import { CopilotKit, useCopilotMessagesContext, useThreads, useCopilotReadable } from "@copilotkit/react-core";
import { useAgent, useConfigureSuggestions, useRenderTool } from "@copilotkit/react-core/v2";
import { CopilotPopup } from "@copilotkit/react-ui";
import "@copilotkit/react-ui/styles.css";
import "./shadow-dom-theme.css";

import type { CartContext, CustomerContext } from "./types";

async function readFileAsRawBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

// pdfjs-dist is intentionally NOT a static import — it'd be flattened into
// the IIFE bundle by `inlineDynamicImports: true` and add ~3-5 MB raw. Instead
// we resolve it from a CDN at runtime *only* when a PDF is actually uploaded.
// The `/* @vite-ignore */` directive tells Vite to leave the import() call as
// a true runtime dynamic import (not a build-time chunk reference).
const PDFJS_VERSION = "5.7.284";
const DEFAULT_PDF_CDN_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/`;

let cachedPdfjs: Promise<any> | null = null;

async function loadPdfjs(cdnBase: string): Promise<any> {
  if (cachedPdfjs) return cachedPdfjs;
  const base = cdnBase.replace(/\/?$/, "/");
  const moduleUrl = base + "pdf.min.mjs";
  cachedPdfjs = import(/* @vite-ignore */ moduleUrl);
  return cachedPdfjs;
}

async function pdfFirstPageToPngBase64(
  file: File,
  pdfCdnBase: string,
  pdfWorkerUrl?: string,
): Promise<string> {
  const pdfjs: any = await loadPdfjs(pdfCdnBase);
  const base = pdfCdnBase.replace(/\/?$/, "/");
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl ?? base + "pdf.worker.min.mjs";

  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const page = await doc.getPage(1);
  const viewport = page.getViewport({ scale: 2.0 });

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d")!;
  await page.render({ canvasContext: ctx, viewport, canvas }).promise;

  const dataUrl = canvas.toDataURL("image/png");
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function makeOnAttachmentUpload(pdfCdnBase: string, pdfWorkerUrl?: string) {
  return async function onAttachmentUpload(file: File) {
    if (file.type === "application/pdf") {
      const base64 = await pdfFirstPageToPngBase64(file, pdfCdnBase, pdfWorkerUrl);
      return { type: "data" as const, value: base64, mimeType: "image/png" };
    }
    const base64 = await readFileAsRawBase64(file);
    return {
      type: "data" as const,
      value: base64,
      mimeType: file.type || "application/octet-stream",
    };
  };
}

const CHAT_OPTIONS = [
  { title: "Track your order", message: "I need to track my order" },
  { title: "Start a return or warranty claim", message: "I want to start a return or warranty claim" },
  { title: "Check battery fitment", message: "I need to check battery fitment for my vehicle" },
  { title: "Get installation help", message: "I need help with battery installation" },
  { title: "Fix a billing or order issue", message: "I have a billing or order issue I need help with" },
  { title: "Cancel or modify an order", message: "I need to cancel or modify my order" },
  { title: "Product questions", message: "I have a question about a product" },
  { title: "Something else", message: "I need help with something else" },
];

function ClearChatListener() {
  const { setMessages } = useCopilotMessagesContext();
  const { setThreadId } = useThreads();

  useEffect(() => {
    function handleClear() {
      setMessages([]);
      setThreadId(crypto.randomUUID());
    }
    window.addEventListener("copilot:clear", handleClear);
    return () => window.removeEventListener("copilot:clear", handleClear);
  }, [setMessages, setThreadId]);

  return null;
}

function WelcomeSuggestions() {
  useConfigureSuggestions({ suggestions: CHAT_OPTIONS });
  return null;
}

// The bridge's emit-tool-calls:false only filters STREAMED tool-call events; the
// run-end MESSAGES_SNAPSHOT still carries the supervisor's specialist calls, and
// react-core >= 1.57 draws a default "<name> · Done" card for any tool call with
// no renderer. A wildcard renderer that draws nothing suppresses it (TC-1/TC-2).
function HideToolCallCards() {
  useRenderTool({ name: "*", render: () => <></> }, []);
  return null;
}

function ShopifyContext({ customer, cart }: { customer?: CustomerContext; cart?: CartContext }) {
  const customerDescription = useMemo(() => {
    if (!customer) return null;
    const parts: string[] = [];
    if (customer.firstName || customer.lastName)
      parts.push(`Name: ${[customer.firstName, customer.lastName].filter(Boolean).join(" ")}`);
    if (customer.email) parts.push(`Email: ${customer.email}`);
    if (customer.id) parts.push(`Shopify Customer ID: ${customer.id}`);
    return parts.length > 0 ? parts.join("; ") : null;
  }, [customer]);

  const cartDescription = useMemo(() => {
    if (!cart || !cart.itemCount) return null;
    const parts = [`${cart.itemCount} item(s)`];
    if (cart.totalPrice != null)
      parts.push(`total ${cart.currency ?? "USD"} ${cart.totalPrice}`);
    return parts.join(", ");
  }, [cart]);

  useCopilotReadable({
    description: "Logged-in Shopify customer identity",
    value: customerDescription ?? "Guest (not logged in)",
  });

  useCopilotReadable({
    description: "Current Shopify cart contents",
    value: cartDescription ?? "Cart is empty",
  });

  return null;
}

const DISCLAIMER_TEXT = "AI can make mistakes. Verify important info.";
const PRIVACY_URL = "https://chromebattery.com/pages/privacy-policy";

function ChatFooterDisclaimer({ shadowRoot }: { shadowRoot?: ShadowRoot }) {
  useEffect(() => {
    // When mounted inside Shadow DOM, querySelector/MutationObserver must
    // target the shadow root — CopilotKit's elements aren't visible from
    // document.body. Fall back to document for non-shadow usage.
    const queryRoot: ParentNode = shadowRoot ?? document;
    const observeTarget: Node = shadowRoot ?? document.body;

    const node = document.createElement("div");
    node.className = "cb-ai-disclaimer";
    const span = document.createElement("span");
    span.textContent = DISCLAIMER_TEXT + " ";
    const link = document.createElement("a");
    link.href = PRIVACY_URL;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = "Privacy Policy";
    node.appendChild(span);
    node.appendChild(link);

    let mounted: HTMLElement | null = null;

    const tryMount = () => {
      queryRoot.querySelectorAll<HTMLElement>(".poweredBy").forEach((el) => el.remove());

      const container = queryRoot.querySelector<HTMLElement>(".copilotKitInputContainer");
      if (container && !container.contains(node)) {
        container.appendChild(node);
        mounted = container;
      } else if (!container && mounted) {
        mounted = null;
      }
    };

    const observer = new MutationObserver(tryMount);
    observer.observe(observeTarget, { childList: true, subtree: true });
    tryMount();

    return () => {
      observer.disconnect();
      if (mounted && node.parentElement === mounted) {
        mounted.removeChild(node);
      }
    };
  }, [shadowRoot]);

  return null;
}

// Identifies the runtime-bridge regenerate/checkpoint fault. Post Lever-C this
// surfaces as a GRACEFUL "No checkpoint found for message" run error (pre-C it
// was "Message not found" + a crash); we match both.
const CHECKPOINT_ERROR_RE = /no checkpoint found|message not found/i;
// Debounce silent rotations so a pathological repeat can't spin a rotate-loop.
const ROTATION_DEBOUNCE_MS = 8000;
// Stuck-stream detection window: generously above the p99 supervisor+RAG
// round-trip; the banner is informational and auto-clears on the next message.
const STUCK_TIMEOUT_MS = 35000;

// Recovers the chat when a run fails. Mirrors the webapp's RunErrorBanner,
// adapted for Shadow DOM (all DOM queries/injection target the shadow root).
//   (A) CHECKPOINT/REGENERATE FAULT → SILENT, customer-invisible recovery:
//       rotate to a fresh threadId WITHOUT clearing the visible chat (CopilotKit
//       keeps messages across a threadId change), so the next message lands on
//       an empty server thread where the regenerate misfire is structurally
//       impossible. A calm auto-clearing "reconnecting…" notice fills the gap.
//       No button, no wiped chat — the customer (a battery shopper) never acts.
//   (B) ANY OTHER FAILURE / SILENT STALL → the original informational banner.
// See PROGRESS.md C5 / Session 17. Backend levers C (bridge) + A (langgraph) are
// shared; this is the widget's port of the frontend recovery (Lever B).
function RunErrorBanner({ shadowRoot, agentId }: { shadowRoot?: ShadowRoot; agentId: string }) {
  const { messages } = useCopilotMessagesContext();
  // MUST pass agentId: bare useAgent() targets agent "default", which this
  // deployment doesn't have ("agent") → it throws after runtime sync, killing
  // the widget's React mount (no chat button renders). See PROGRESS.md Session 17.
  const { agent } = useAgent({ agentId });
  const { setThreadId } = useThreads();
  const bannerRef = useRef<HTMLElement | null>(null);
  const lastRotationRef = useRef(0);

  const removeBanner = useCallback(() => {
    const el = bannerRef.current;
    if (el && el.parentElement) el.parentElement.removeChild(el);
    bannerRef.current = null;
  }, []);

  // In Shadow DOM, .copilotKitInputContainer isn't visible from document —
  // query within the shadow root (fall back to document for non-shadow usage).
  const insertAboveInput = useCallback(
    (node: HTMLElement) => {
      const queryRoot: ParentNode = shadowRoot ?? document;
      const input = queryRoot.querySelector<HTMLElement>(".copilotKitInputContainer");
      if (!input || !input.parentElement) return null;
      input.parentElement.insertBefore(node, input);
      return node;
    },
    [shadowRoot],
  );

  const showBanner = useCallback(() => {
    if (bannerRef.current && bannerRef.current.isConnected) return;
    const div = document.createElement("div");
    div.className = "cb-ai-error-banner";
    div.setAttribute("role", "alert");

    const text = document.createElement("span");
    text.className = "cb-ai-error-banner__text";
    text.textContent = "Hmm — your last message may not have gone through. Please try sending it again.";

    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.className = "cb-ai-error-banner__dismiss";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.textContent = "×";
    dismiss.addEventListener("click", removeBanner);

    div.appendChild(text);
    div.appendChild(dismiss);
    if (insertAboveInput(div)) bannerRef.current = div;
  }, [removeBanner, insertAboveInput]);

  const showReconnectNotice = useCallback(() => {
    const div = document.createElement("div");
    div.className = "cb-ai-reconnect-notice";
    div.setAttribute("role", "status");
    div.textContent = "One moment — reconnecting…";
    if (insertAboveInput(div)) {
      window.setTimeout(() => {
        if (div.parentElement) div.parentElement.removeChild(div);
      }, 4000);
    }
  }, [insertAboveInput]);

  const handleRunError = useCallback(
    (errMsg?: string) => {
      if (errMsg && CHECKPOINT_ERROR_RE.test(errMsg)) {
        const now = Date.now();
        if (now - lastRotationRef.current < ROTATION_DEBOUNCE_MS) return;
        lastRotationRef.current = now;
        removeBanner();
        setThreadId(crypto.randomUUID()); // fresh thread; visible chat preserved
        showReconnectNotice();
        return;
      }
      showBanner();
    },
    [removeBanner, setThreadId, showReconnectNotice, showBanner],
  );

  // Trigger 1 — AG-UI run failures; the error string decides rotation vs banner.
  useEffect(() => {
    if (!agent) return;
    const sub = agent.subscribe({
      onRunFailed: (p: any) => handleRunError(p?.error?.message),
      onRunErrorEvent: (p: any) => handleRunError(p?.event?.message),
      onRunInitialized: () => removeBanner(),
      onTextMessageStartEvent: () => removeBanner(),
    });
    return () => sub.unsubscribe();
  }, [agent, handleRunError, removeBanner]);

  // Trigger 2 — stuck-stream timer (silent mid-stream death). Banner only.
  useEffect(() => {
    const last: any = messages[messages.length - 1];
    const lastIsUser =
      !!last &&
      (last.role === "user" || last.role === "human" || last.type === "user" || last.type === "human");
    if (!lastIsUser) return;
    const timer = setTimeout(showBanner, STUCK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [messages, showBanner]);

  // Manual escape hatch for testing / programmatic triggers.
  useEffect(() => {
    const onForce = () => showBanner();
    window.addEventListener("copilot:run-error", onForce);
    return () => {
      window.removeEventListener("copilot:run-error", onForce);
      removeBanner();
    };
  }, [showBanner, removeBanner]);

  return null;
}

export type WidgetProps = {
  runtimeUrl?: string;
  publicApiKey?: string;
  agent: string;
  pdfCdnBase?: string;
  pdfWorkerUrl?: string;
  customer?: CustomerContext;
  cart?: CartContext;
  shadowRoot?: ShadowRoot;
};

function App({ runtimeUrl, publicApiKey, agent, pdfCdnBase, pdfWorkerUrl, customer, cart, shadowRoot }: WidgetProps) {
  const transport = runtimeUrl ? { runtimeUrl } : { publicApiKey: publicApiKey ?? "" };
  const onAttachmentUpload = makeOnAttachmentUpload(
    pdfCdnBase ?? DEFAULT_PDF_CDN_BASE,
    pdfWorkerUrl,
  );

  return (
    <CopilotKit {...transport} agent={agent} enableInspector={false}>
      <ClearChatListener />
      <WelcomeSuggestions />
      <HideToolCallCards />
      <ShopifyContext customer={customer} cart={cart} />
      <ChatFooterDisclaimer shadowRoot={shadowRoot} />
      <RunErrorBanner shadowRoot={shadowRoot} agentId={agent} />
      <CopilotPopup
        defaultOpen={false}
        clickOutsideToClose
        attachments={{
          enabled: true,
          accept: "image/*,application/pdf",
          onUpload: onAttachmentUpload,
        }}
        labels={{
          initial: "Hi, I’m Claire — how can I help you today?",
          title: "Claire (AI Assistant)",
        }}
      />
    </CopilotKit>
  );
}

export function mountWidget(el: HTMLElement, props: WidgetProps): Root {
  const root = createRoot(el);
  root.render(<App {...props} />);
  return root;
}
